import { Injectable } from '@angular/core';
import { SurveyStore } from './survey.store';
import { SurveyQuery } from './survey.query';
import { ApiService } from '../../shared/services/core/api.service';
import { of, Observable, firstValueFrom } from 'rxjs';
import { Survey } from './models/survey.model';
import { tap, switchMap, mapTo } from 'rxjs/operators';
import { SurveyAnswerDto } from './models/survey-answer-dto.model';
import { ScorePredictorAnswer } from './models/score-predictor-answer.model';
import { ScorePredictorStatus } from './models/score-predictor-status.enum';

@Injectable({ providedIn: 'root' })
export class SurveyService {
  constructor(private surveyStore: SurveyStore, private surveyQuery: SurveyQuery, private api: ApiService) {}

  /** Fetches all the surveys and puts them in the store
   * @showAnswered {boolean} show items that have been answered
   * @showComingSoon {boolean} show items that have not started
   * @showEndedItems {boolean} show items passed end date
   * @showOutOfStock {boolean} show out of stock items
   *
   * @returns Survey[]
   */
  fetchAllSurveys(
    showComingSoon: boolean = true,
    showOutOfStock: boolean = true,
    showEndedItems: boolean = true,
    showAnswered: boolean = true
  ): Observable<null> {
    const request$ = this.api
      .get<Survey>(
        'surveys/withanswers?showComingSoon=' +
          showComingSoon +
          '&showOutOfStock=' +
          showOutOfStock +
          '&showEndedItems=' +
          showEndedItems +
          '&showAnswered=' +
          showAnswered
      )
      .pipe(
        tap((surveys) => {
          surveys.forEach((survey) => {
            survey.ShowToUser = !survey.HasBeenAnsweredByUser;
            survey.ShowAtTop = false;
          });
          this.surveyStore.upsertMany(surveys);
        }),
        mapTo(null)
      );

    return this.surveyQuery.getHasCache() ? of(null) : request$;
  }

  /** Fetches a specific survey (ignoring cache) and puts it in the store. */
  fetchSurveyById(id: number): Observable<null> {
    const request$: Observable<null> = this.api.getById<Survey>('surveys', id.toString()).pipe(
      tap((survey) => {
        this.surveyStore.upsert(id, survey);
      }),
      mapTo(null)
    );

    return request$;
  }

  /** hide the current question after a specified amount of time */
  hideToUserAfterTimeout(timeoutMs: number, surveyId: number) {
    setTimeout(async () => {
      this.surveyStore.update(surveyId, { ShowToUser: false, ShowAtTop: false });
    }, timeoutMs);
  }

  /** Submit an array of survey answers */
  async postSurveyAnswers(surveyId: number, surveyAnswers: SurveyAnswerDto[]): Promise<null> {
    this.surveyStore.update(surveyId, { ShowAtTop: true });
    return await firstValueFrom(
      this.api
        .postWithArray<SurveyAnswerDto>('surveys/submitanswers', surveyAnswers)
        .pipe(switchMap(() => this.fetchSurveyById(surveyId)))
    );
  }

  async postScorePredictorAnswers(surveyId: number, scorePredictorAnswers: ScorePredictorAnswer[]): Promise<null> {
    this.surveyStore.update(surveyId, { ShowAtTop: true });
    return await firstValueFrom(
      this.api
        .postWithArray<ScorePredictorAnswer>('surveys/submitscorepredictor', scorePredictorAnswers)
        .pipe(switchMap(() => this.fetchAllSurveys(true, true, false, true)))
    );
  }

  removeSurvey(surveyId: number) {
    this.surveyStore.remove(surveyId);
  }

  scorePredictorStatus(survey: Survey): ScorePredictorStatus {
    if (survey.SeparateEntryWindow) {
      if (survey.RequestTime > survey.EntryStartDate) {
        if (survey.HasBeenAnsweredByUser) {
          return ScorePredictorStatus.Answered;
        } else if (survey.EntryEndDate < survey.RequestTime) {
          this.removeSurvey(survey.SurveyId);
        } else {
          return ScorePredictorStatus.EntryOpen;
        }
      } else {
        return ScorePredictorStatus.ComingSoon;
      }
    } else {
      if (survey.RequestTime > survey.StartDate && survey.RequestTime < survey.EndDate) {
        if (survey.HasBeenAnsweredByUser) {
          return ScorePredictorStatus.Answered;
        } else {
          return ScorePredictorStatus.EntryOpen;
        }
      } else {
        if (survey.RequestTime < survey.StartDate) {
          return ScorePredictorStatus.ComingSoon;
        } else if (survey.RequestTime > survey.EndDate) {
          this.removeSurvey(survey.SurveyId);
        }
      }
    }
    return ScorePredictorStatus.ComingSoon;
  }
}
